import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Form, Input, Select, Switch, message } from "antd";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";

const { TextArea } = Input;
const { Option } = Select;

const TemplateModal = ({ visible, onCancel, onSuccess, template }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  const {
    academicYears,
    currentAcademicYear,
    fetchAcademicYears,
    fetchCurrentAcademicYear,
  } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const fetchClasses = useCallback(async () => {
    setLoadingClasses(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.klass,
        null,
        navigate,
      );

      if (response.ok) {
        setClasses(response.data.results || response.data || []);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setLoadingClasses(false);
    }
  }, [navigate]);

  // Fetch data when modal opens
  useEffect(() => {
    if (visible) {
      fetchClasses();
      fetchAcademicYears();
      fetchCurrentAcademicYear();
      fetchTerms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Set form values when modal opens or template changes
  useEffect(() => {
    if (visible) {
      if (template) {
        // Edit mode
        form.setFieldsValue({
          name: template.name,
          school_class: template.school_class,
          term: template.term,
          acadyr: template.acadyr,
          description: template.description || "",
          is_active: template.is_active === 1,
        });
      } else {
        // Create mode
        form.resetFields();
        form.setFieldsValue({
          is_active: true,
        });
      }
    }
  }, [visible, template, form]);

  // Set default academic year when it loads (separate effect to avoid infinite loop)
  useEffect(() => {
    if (visible && !template && currentAcademicYear?.acadyr) {
      form.setFieldsValue({ acadyr: currentAcademicYear.acadyr });
    }
  }, [visible, template, form, currentAcademicYear]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        name: values.name,
        school_class: values.school_class,
        term: values.term,
        acadyr: values.acadyr,
        description: values.description || null,
        is_active: values.is_active ? 1 : 0,
      };

      const method = template ? "PUT" : "POST";
      const url = template
        ? domainSettings.urls.bot.requirementTemplateDetail.replace(
            ":id",
            template.id,
          )
        : domainSettings.urls.bot.requirementTemplates;

      const response = await Utilities.fetchWithAuth(
        method,
        url,
        payload,
        navigate,
      );

      if (response.ok) {
        message.success(
          template
            ? "Template updated successfully"
            : "Template created successfully",
        );
        form.resetFields();
        onSuccess();
      } else {
        Utilities.showError(response, "Failed to save template");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={template ? "Edit Template" : "Create Template"}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={700}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ is_active: true }}
      >
        <Form.Item
          label="Template Name"
          name="name"
          rules={[
            { required: true, message: "Please enter template name" },
            { max: 200, message: "Name cannot exceed 200 characters" },
          ]}
        >
          <Input placeholder="e.g., Form 1 BOT Requirements 2025" />
        </Form.Item>

        <Form.Item
          label="School Class"
          name="school_class"
          rules={[{ required: true, message: "Please select a class" }]}
        >
          <Select
            placeholder="Select class"
            loading={loadingClasses}
            showSearch
            optionFilterProp="children"
          >
            {classes.map((klass) => (
              <Option key={klass.id} value={klass.id}>
                {klass.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Term"
          name="term"
          rules={[{ required: true, message: "Please select term" }]}
        >
          <Select placeholder="Select term">
            {Array.isArray(terms) &&
              terms.map((term) => (
                <Option key={term.id} value={term.id}>
                  {term.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Academic Year"
          name="acadyr"
          rules={[{ required: true, message: "Please select academic year" }]}
        >
          <Select
            placeholder="Select academic year"
            showSearch
            optionFilterProp="children"
          >
            {Array.isArray(academicYears) &&
              academicYears.map((year) => (
                <Option key={year.id} value={year.acadyr}>
                  {year.acadyr}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea
            rows={3}
            placeholder="Optional description of this template"
          />
        </Form.Item>

        <Form.Item label="Active" name="is_active" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TemplateModal;
